import React, { useState, useEffect } from "react";
import {
  Outlet,
  useNavigate,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import {
  Check,
  ArrowLeft,
  FileText,
  MessageSquare,
  BookOpen,
  Settings,
  MessageCircle,
  Smartphone,
  QrCode,
  Rocket,
  Cpu,
  Brain,
  Loader2,
} from "lucide-react";
import { useAgent } from "../../context/AgentContext";

const steps = [
  { id: 1, name: "Name", path: "name", icon: FileText },
  { id: 2, name: "Behavior", path: "behavior", icon: MessageSquare },
  { id: 3, name: "Knowledge", path: "knowledge", icon: BookOpen },
  { id: 4, name: "AI Model", path: "model", icon: Cpu },
  { id: 5, name: "Features", path: "features", icon: Settings },
  { id: 6, name: "Training", path: "training", icon: Brain },
  { id: 7, name: "Test AI", path: "test", icon: MessageCircle },
  { id: 8, name: "Connect WhatsApp", path: "connect", icon: Smartphone },
  { id: 9, name: "Pair QR", path: "pair", icon: QrCode },
  { id: 10, name: "Go Live", path: "golive", icon: Rocket },
];

export default function CreateAgentLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { currentAgent, loadAgent, updateStepProgress, isLoading } = useAgent();

  const currentPath = location.pathname.split("/").pop();
  const currentStepIndex = steps.findIndex((s) => s.path === currentPath);
  const currentStep = currentStepIndex === -1 ? 0 : currentStepIndex;

  // Track the highest step reached to allow navigation back to any visited step
  const [highestStepReached, setHighestStepReached] = useState(0);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  // Load existing agent if resuming (agentId in URL params)
  useEffect(() => {
    const agentId = searchParams.get("agentId");
    if (agentId && !currentAgent) {
      loadAgent(agentId)
        .then((agent) => {
          // Set highest step from saved progress
          const savedStep = agent?.current_step || 0;
          setHighestStepReached(savedStep);
          setInitialLoadDone(true);

          // Navigate to the saved step
          if (savedStep > 0 && savedStep < steps.length) {
            navigate(`/create/${steps[savedStep].path}?agentId=${agentId}`, {
              replace: true,
            });
          }
        })
        .catch(() => {
          setInitialLoadDone(true);
        });
    } else {
      setInitialLoadDone(true);
    }
  }, [searchParams, currentAgent, loadAgent, navigate]);

  useEffect(() => {
    if (location.pathname === "/create" || location.pathname === "/create/") {
      navigate("/create/name", { replace: true });
    }
  }, [location.pathname, navigate]);

  // Update highest step reached when user progresses
  useEffect(() => {
    if (currentStep > highestStepReached) {
      setHighestStepReached(currentStep);
      // Save progress to backend
      if (currentAgent?.id) {
        updateStepProgress(currentStep + 1); // +1 because steps are 1-indexed in backend
      }
    }
  }, [currentStep, highestStepReached, currentAgent?.id, updateStepProgress]);

  const goToStep = (stepPath) => {
    const agentId = searchParams.get("agentId") || currentAgent?.id;
    if (agentId) {
      navigate(`/create/${stepPath}?agentId=${agentId}`);
    } else {
      navigate(`/create/${stepPath}`);
    }
  };

  const goNext = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      // Update highest step when moving forward
      if (nextStep > highestStepReached) {
        setHighestStepReached(nextStep);
      }
      goToStep(steps[nextStep].path);
    }
  };

  const goPrev = () => {
    if (currentStep > 0) {
      goToStep(steps[currentStep - 1].path);
    } else {
      navigate("/dashboard");
    }
  };

  // Show loading while loading agent
  if (!initialLoadDone && isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-4" />
          <p className="text-text-muted">Loading agent...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="w-72 bg-white border-r border-border flex flex-col h-screen sticky top-0">
        {/* Logo */}
        <div className="p-6 border-b border-border">
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 text-text-muted hover:text-text-primary transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Dashboard</span>
          </button>
        </div>

        {/* Steps */}
        <nav className="flex-1 p-4 overflow-y-auto">
          <h2 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
            Create New Agent
          </h2>
          <p className="text-xs text-text-muted mb-4 flex items-center gap-1">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-success animate-pulse"></span>
            Takes about 10 minutes to complete
          </p>
          <div className="space-y-1">
            {steps.map((step, index) => {
              const isCompleted = index < currentStep;
              const isActive = index === currentStep;
              const isAccessible = index <= highestStepReached;
              const Icon = step.icon;

              return (
                <button
                  key={step.id}
                  onClick={() => isAccessible && goToStep(step.path)}
                  disabled={!isAccessible}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left
                    transition-all duration-200
                    ${
                      isActive
                        ? "bg-primary/10 text-primary"
                        : isAccessible
                          ? "text-text-primary hover:bg-gray-50 cursor-pointer"
                          : "text-text-muted cursor-not-allowed"
                    }
                  `}
                >
                  <div
                    className={`
                    step-indicator
                    ${
                      isCompleted
                        ? "completed"
                        : isActive
                          ? "active"
                          : "pending"
                    }
                  `}
                  >
                    {isCompleted ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <span>{step.id}</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <span
                      className={`text-sm font-medium block ${
                        isActive ? "text-primary" : ""
                      }`}
                    >
                      {step.name}
                    </span>
                    {isCompleted && (
                      <span className="text-xs text-success inline-flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Done
                      </span>
                    )}
                  </div>
                  {isActive && <Icon className="w-4 h-4 text-primary" />}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Progress */}
        <div className="p-4 border-t border-border">
          <div className="flex items-center justify-between text-sm text-text-muted mb-2">
            <span>Progress</span>
            <span>{Math.round(((currentStep + 1) / steps.length) * 100)}%</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-primary to-accent transition-all duration-500"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col">
        <div className="flex-1 overflow-auto">
          <Outlet context={{ goNext, goPrev, currentStep }} />
        </div>
      </main>
    </div>
  );
}
